import React, { useState, useRef, useEffect, useCallback } from 'react';
import { injectText } from '../voiceInjector';

// Module-level recording guard — shared across all VoiceButton instances so
// the TopBar and FullscreenWrapper buttons can never record simultaneously.
let _isAnyRecording = false;

// ---------------------------------------------------------------------------
// VAD / recording config
// ---------------------------------------------------------------------------
const SILENCE_THRESHOLD = 0.012;  // RMS amplitude below this → silence
const SILENCE_DELAY_MS  = 1500;   // ms of silence after speech → auto-stop
const MAX_RECORD_MS     = 30000;  // hard cap on recording length
const TARGET_SAMPLE_RATE = 16000; // Whisper expects 16 kHz mono PCM

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function VoiceButton() {
  const [state,   setState]   = useState('idle'); // see states below
  const [loadPct, setLoadPct] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  // Refs that need to be read inside async callbacks / rAF without stale closure
  const stateRef      = useRef('idle');
  const workerRef     = useRef(null);
  const buttonRef     = useRef(null);
  const isMountedRef  = useRef(true);

  // Audio resources — all cleaned up together
  const streamRef      = useRef(null);
  const ctxRef         = useRef(null);
  const analyserRef    = useRef(null);
  const recorderRef    = useRef(null);
  const chunksRef      = useRef([]);
  const hasSpeechRef   = useRef(false);
  const rafRef         = useRef(null);
  const silenceRef     = useRef(null);
  const maxTimerRef    = useRef(null);

  // Keep stateRef in sync with React state
  const set = useCallback((s) => {
    stateRef.current = s;
    setState(s);
  }, []);

  // ---------------------------------------------------------------------------
  // Cleanup all audio resources
  // ---------------------------------------------------------------------------
  const cleanup = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(silenceRef.current);
    clearTimeout(maxTimerRef.current);
    rafRef.current    = null;
    silenceRef.current = null;
    maxTimerRef.current = null;
    hasSpeechRef.current = false;

    // Stop MediaRecorder before nulling it so onstop fires and the browser
    // releases the mic resource cleanly.
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    // Do NOT clear chunksRef here — onstop fires asynchronously after stop() and
    // needs the chunks that ondataavailable delivered.  onstop clears the buffer
    // itself (line ~207), and startListening resets it before each new recording.

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (ctxRef.current) {
      ctxRef.current.close().catch(() => {});
      ctxRef.current = null;
    }
    analyserRef.current = null;

    // Release global recording mutex so the other button instance can record.
    _isAnyRecording = false;
  }, []);

  // ---------------------------------------------------------------------------
  // Worker lifecycle — created lazily on first click (see ensureWorker below),
  // so the ~145 MB model download only starts when the user initiates dictation.
  // ---------------------------------------------------------------------------
  // Set when the user's click triggered the model load, so listening starts
  // automatically once the model is ready instead of requiring a second click.
  const pendingStartRef = useRef(false);

  useEffect(() => {
    // Reset mounted flag — necessary because React StrictMode runs effects twice
    // (mount → cleanup → remount) on the same component instance in development.
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // cleanup() first so recorder.stop() is called before the worker is killed.
      // onstop fires asynchronously — isMountedRef being false prevents it from
      // posting to the (about-to-be) terminated worker.
      cleanup();
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [cleanup]);

  // ---------------------------------------------------------------------------
  // Stop recording and ship audio to the worker for transcription
  // ---------------------------------------------------------------------------
  const stopAndTranscribe = useCallback(() => {
    // Cancel the VAD loop first to prevent re-entry
    cancelAnimationFrame(rafRef.current);
    clearTimeout(silenceRef.current);
    clearTimeout(maxTimerRef.current);
    rafRef.current     = null;
    silenceRef.current  = null;
    maxTimerRef.current = null;

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop(); // fires recorder.onstop which does the heavy lifting
    }

    // Stop mic stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (ctxRef.current) {
      ctxRef.current.close().catch(() => {});
      ctxRef.current = null;
    }
    analyserRef.current  = null;
    hasSpeechRef.current = false;
    _isAnyRecording = false;

    set('processing');
  }, [set]);

  // ---------------------------------------------------------------------------
  // Start mic + VAD
  // ---------------------------------------------------------------------------
  const startListening = useCallback(async () => {
    if (_isAnyRecording) return; // another VoiceButton instance is already recording
    _isAnyRecording = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount:     1,
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate:       { ideal: 48000 },
        },
      });
      streamRef.current = stream;

      // AudioContext for VAD (AnalyserNode only — no audio output)
      const ctx = new AudioContext();
      ctxRef.current = ctx;

      const source   = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize              = 1024;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      analyserRef.current = analyser;

      // MediaRecorder captures audio as a compressed blob (WebM/Opus).
      // We decode it back to PCM in onstop via decodeAudioData.
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current   = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        // Component may have unmounted while the recorder was stopping.
        if (!isMountedRef.current) return;

        const chunks = chunksRef.current.slice();
        chunksRef.current = [];

        if (chunks.length === 0) {
          set('ready');
          return;
        }

        try {
          // 1. Decode compressed audio → raw PCM AudioBuffer
          const blob      = new Blob(chunks, { type: recorder.mimeType });
          const arrayBuf  = await blob.arrayBuffer();
          const decodeCtx = new AudioContext();
          let decoded;
          try {
            decoded = await decodeCtx.decodeAudioData(arrayBuf);
          } finally {
            // Always close — prevents AudioContext leak on decodeAudioData failure.
            decodeCtx.close();
          }

          // 2. Resample to 16 kHz mono via OfflineAudioContext
          const outLength = Math.ceil(
            decoded.length * TARGET_SAMPLE_RATE / decoded.sampleRate
          );
          const offCtx = new OfflineAudioContext(1, outLength, TARGET_SAMPLE_RATE);
          const src    = offCtx.createBufferSource();
          src.buffer   = decoded;
          src.connect(offCtx.destination);
          src.start();

          const rendered = await offCtx.startRendering();
          const float32  = rendered.getChannelData(0);

          // 3. Send to worker — transfer the buffer (zero-copy)
          if (isMountedRef.current) {
            workerRef.current.postMessage(
              { type: 'transcribe', data: float32 },
              [float32.buffer],
            );
          }
        } catch (err) {
          if (isMountedRef.current) {
            setErrorMsg('Audio processing failed: ' + err.message);
            set('error');
          }
        }
      };

      // Collect chunks every 250 ms so onstop receives them all
      recorder.start(250);
      set('listening');

      // -----------------------------------------------------------------------
      // VAD loop — runs in rAF, lightweight RMS check
      // -----------------------------------------------------------------------
      const vadBuf = new Float32Array(analyser.fftSize);

      const tick = () => {
        if (stateRef.current !== 'listening') return;

        analyser.getFloatTimeDomainData(vadBuf);
        let sum = 0;
        for (let i = 0; i < vadBuf.length; i++) sum += vadBuf[i] * vadBuf[i];
        const rms = Math.sqrt(sum / vadBuf.length);

        if (rms > SILENCE_THRESHOLD) {
          hasSpeechRef.current = true;
          clearTimeout(silenceRef.current);
          silenceRef.current = null;
        } else if (hasSpeechRef.current && !silenceRef.current) {
          // Speech was detected and now it's quiet — start the silence timer
          silenceRef.current = setTimeout(stopAndTranscribe, SILENCE_DELAY_MS);
        }

        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);

      // Safety cap — never record more than MAX_RECORD_MS
      maxTimerRef.current = setTimeout(stopAndTranscribe, MAX_RECORD_MS);

    } catch (err) {
      // cleanup() releases _isAnyRecording and frees any partially-allocated
      // resources (stream, AudioContext) that may have been created before the
      // error — e.g. if getUserMedia succeeded but MediaRecorder construction threw.
      cleanup();
      let msg;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        msg = 'Microphone access denied — allow microphone access in browser settings.';
      } else if (err.name === 'NotFoundError') {
        msg = 'No microphone found — please connect a microphone.';
      } else {
        msg = 'Could not open microphone: ' + err.message;
      }
      setErrorMsg(msg);
      set('error');
    }
  }, [set, stopAndTranscribe, cleanup]);

  // ---------------------------------------------------------------------------
  // Create the worker on demand — nothing is downloaded until the user clicks.
  // Also means only the clicked button instance loads the model, not both.
  // ---------------------------------------------------------------------------
  const ensureWorker = useCallback(() => {
    if (workerRef.current) return workerRef.current;

    // Webpack 5 (CRA 5) bundles this as a separate worker chunk with full npm resolution.
    const worker = new Worker(new URL('../stt.worker.js', import.meta.url));
    workerRef.current = worker;

    worker.onmessage = ({ data: msg }) => {
      switch (msg.type) {
        case 'progress':
          // Show overall % across all downloaded model files
          if (msg.data.total > 0) {
            setLoadPct(msg.data.progress);
          }
          break;

        case 'ready':
          if (pendingStartRef.current) {
            // The user clicked to dictate — start listening right away.
            pendingStartRef.current = false;
            startListening();
          } else {
            set('ready');
          }
          break;

        case 'result':
          if (msg.data) injectText(msg.data);
          set('ready');
          break;

        case 'error':
          pendingStartRef.current = false;
          setErrorMsg(msg.data || 'Unknown error');
          set('error');
          break;

        default:
          break;
      }
    };

    // If the worker crashes (e.g. WASM OOM during inference) it posts no result
    // message, so onerror is the only signal we get.  Without this the component
    // would hang in 'processing' forever.
    worker.onerror = (e) => {
      if (!isMountedRef.current) return;
      pendingStartRef.current = false;
      setErrorMsg('Worker crashed: ' + (e.message || 'Unknown error'));
      set('error');
    };

    return worker;
  }, [set, startListening]);

  // ---------------------------------------------------------------------------
  // Handle button click
  // ---------------------------------------------------------------------------
  const handleClick = useCallback(() => {
    switch (state) {
      case 'idle':
        // First use — load the model, then start listening automatically.
        pendingStartRef.current = true;
        set('loading');
        setLoadPct(0);
        ensureWorker().postMessage({ type: 'load' });
        break;
      case 'ready':
        startListening();
        break;
      case 'listening':
        stopAndTranscribe();
        break;
      case 'error':
        // Retry loading
        setErrorMsg('');
        set('loading');
        setLoadPct(0);
        ensureWorker().postMessage({ type: 'load' });
        break;
      default:
        break;
    }
  }, [state, startListening, stopAndTranscribe, set, ensureWorker]);

  // ---------------------------------------------------------------------------
  // Render — mirrors the TopBar button style used for Tutorials / Transfer files
  // ---------------------------------------------------------------------------
  const isDisabled = state === 'loading' || state === 'processing';

  let icon  = 'fa fa-microphone';
  let label = ' Dictate';
  let iconStyle = {};
  let title = '';

  switch (state) {
    case 'idle':
      title = 'Click to start dictating (speech model loads on first use)';
      break;
    case 'loading':
      label    = ` Loading… ${loadPct}%`;
      iconStyle = { opacity: 0.5 };
      title    = `Loading speech model (${loadPct}%)`;
      break;
    case 'ready':
      title = 'Click to start dictating';
      break;
    case 'listening':
      label     = ' Listening…';
      iconStyle  = { color: '#e53e3e' };
      title     = 'Listening — click to stop early';
      break;
    case 'processing':
      icon  = 'fa fa-spinner fa-spin';
      label = ' Processing…';
      title = 'Transcribing audio…';
      break;
    case 'error':
      icon      = 'fa fa-microphone-slash';
      label     = ' Error';
      iconStyle  = { color: '#e53e3e' };
      title     = errorMsg || 'Error — click to retry';
      break;
    default:
      break;
  }

  // Keep Bootstrap tooltip in sync — it caches `title` as `data-original-title`
  // on init and never re-reads the attribute, so React's updates are invisible.
  useEffect(() => {
    if (!buttonRef.current) return;
    const $btn = window.$(buttonRef.current);
    if ($btn.attr('data-original-title') !== undefined) {
      $btn.attr('data-original-title', title);
    }
  }, [title]);

  return (
    <button
      ref={buttonRef}
      type="button"
      className="btn control-buttons"
      onClick={handleClick}
      disabled={isDisabled}
      title={title}
    >
      <i className={icon} aria-hidden="true" style={iconStyle}></i>
      {label}
    </button>
  );
}
